import { Create } from '@refinedev/antd';

import {
  Button,
  Card,
  Cascader,
  Descriptions,
  Divider,
  Form,
  Input,
  message,
  Select,
  Skeleton,
  Tag,
  Tooltip,
  TreeSelect,
} from 'antd';

import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';
import { useWindowSize } from 'rooks';
import Xarrow, { useXarrow, Xwrapper } from 'react-xarrows';
import { Client, dbCols, portalEn, User } from '@cc/schema';
import type { DefaultOptionType } from 'antd/es/cascader';
import {
  EyeInvisibleOutlined,
  EyeOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Conceptualization } from '../../schema/be-graphql-generated';
import * as Sentry from '@sentry/react';
import { useClientOptions } from '../../hooks/client-options';
import { xarrowProps } from '../../helpers';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { recursivelyDisableIfChildren } from '../sessions/create/ProgressNote';
import useStyles from './styles.style';
import { useLocation } from 'react-router-dom';
import { SelectClient } from '../../components/SelectClient';
import { DateFnsDatePicker } from '../../components/Pickers';
import { parseISO } from 'date-fns';

type SchemaTherapySchemas = typeof portalEn.pages.conceptualization.schemas;

export const CreateConceptualization = () => {
  const location = useLocation();
  const { styles } = useStyles();

  const locationClientId = (useMemo(
    () =>
      qs.parse(
        location.search.charAt(0) === '?'
          ? location.search.slice(1)
          : location.search,
      ),
    [location.search],
  )?.clientId || '') as string;

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  const { t, i18n } = useTranslation();

  const { clientsLoading } = useClientOptions();

  const initialValues = useMemo(() => {
    return {
      firstName: '',
      lastName: '',
      gender: undefined as undefined | number | null,
      dob: null as Date | null,
      education: undefined as undefined | number,
      maritalStatus: undefined as undefined | number,
      clientProfileOther: '',
      negativePastExperience: '',
      triggerEvent: '',
      geneticPredisposition: '',
      deepBeliefs: '',
      triggers: '',
      problems: [] as string[],
      lifeRules: '',
      goals: '',
      resources: '',
      schemas: [],
    };
  }, []);

  const [form] = Form.useForm<typeof initialValues>();

  const {
    data: clientData,
    isLoading: clientLoading,
    refetch: refetchClient,
    error: clientError,
  } = useCustom<Client>({
    url: '',
    method: 'get',
    meta: {
      operation: 'client',
      fields: ['_id', 'firstName', 'lastName', 'email', 'diagnoses'],
      variables: {
        id: {
          name: 'id',
          type: 'ID',
          value: clientId || '',
        },
      },
    },
  });

  const {
    data: conceptualizationData,
    isLoading: conceptualizationLoading,
    refetch: refetchConceptualization,
    error: conceptualizationError,
  } = useCustom<Conceptualization>({
    url: '',
    method: 'get',
    meta: {
      operation: 'conceptualization',
      fields: [
        '_id',
        'dob',
        'gender',
        'education',
        'maritalStatus',
        'clientProfileOther',
        'negativePastExperience',
        'triggerEvent',
        'geneticPredisposition',
        'deepBeliefs',
        'triggers',
        'problems',
        'lifeRules',
        'goals',
        'resources',
      ],

      variables: {
        clientId: {
          name: 'clientId',
          type: 'ID',
          value: clientId || '',
        },
      },
    },
  });

  useEffect(() => {
    const loadConceptualization = async () => {
      if (clientId) {
        const res = await refetchConceptualization();
        const currentValues = form.getFieldsValue();

        if (res?.data?.data) {
          // @ts-ignore
          form.setFieldsValue({
            ...currentValues,
            // @ts-ignore
            ...(res?.data?.data || {}),
            dob: res?.data?.data?.dob ? parseISO(res?.data?.data?.dob) : null,
          });

          therapistActivitySend({
            type: 'ENTITY_CLIENT_ID_CHANGED',
            clientId,
          });
        }
      }
    };
    loadConceptualization();

    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const client = clientData?.data;

  const [diagnoses, setDiagnoses] = useState<string[]>([]);

  useEffect(() => {
    const firstName = form.getFieldValue('firstName');
    const lastName = form.getFieldValue('lastName');

    if (
      client &&
      ((!firstName && client.firstName) || (!lastName && client?.lastName))
    ) {
      form?.setFieldsValue({
        firstName: client.firstName || '',
        lastName: client.lastName || '',
      });
    }

    if (client?.diagnoses?.length) {
      setDiagnoses((client?.diagnoses || []) as unknown as string[]);
    }
  }, [client]);

  const schemas = useMemo(() => {
    return t('pages.conceptualization.schemas', {
      returnObjects: true,
    }) as SchemaTherapySchemas;
  }, [i18n.language]);

  const [obfuscate, setObfuscate] = useState(true);

  useEffect(() => {
    if (!obfuscate) {
      const handle = setTimeout(() => {
        setObfuscate(true);
      }, 1000 * 60);
      return () => clearTimeout(handle);
    }
  }, [obfuscate]);

  /**
   * The autosave era ALSO saved on unmount — a `useLayoutEffect` cleanup flushed the whole form
   * whenever the component left the tree. Removed with the explicit Save (founder found it in the
   * prod pilot, 2026-09-01: ONE Save click, THREE identical events — the other two were
   * unmount-saves as he navigated the portal). Under explicit Save it was wrong twice over: it
   * wrote without the user asking, and it made the unsaved-changes warning a lie — confirming
   * "discard" unmounted the form, and the unmount SAVED the very changes being discarded.
   */

  const options = useMemo(() => {
    return Object.entries(schemas).map(([domainKey, domainValue]) => {
      const domain = {
        value: domainValue.name,
        label: domainValue.name,
        children: domainValue.children.map((schema) => {
          const option = {
            value: schema.name,
            label: (
              <Tooltip title={schema.tip}>
                <div>{schema.name}</div>
              </Tooltip>
            ),

            children: schema.copings.map((coping) => {
              const option = {
                value: coping.name,
                label: (
                  <Tooltip title={coping.tip}>
                    <div>{coping.name}</div>
                  </Tooltip>
                ),
              };
              return option;
            }),
          };
          return option;
        }),
      };
      return domain;
    });
  }, [JSON.stringify(schemas)]);

  const updateXarrow = useXarrow();

  const triggerScroll = () => {
    window.scrollBy(0, 1);
    window.scrollBy(0, -1);
  };

  const {
    mutateAsync: upsertConceptualization,
    data: upsertConceptualizationData,
    isLoading: upsertConceptualizationLoading,
  } = useCustomMutation<{ conceptualization: Conceptualization }>();

  // Refine's unsaved-changes contract; `UnsavedChangesNotifier` is mounted in App.tsx.
  const { setWarnWhen } = useWarnAboutChange();
  /** Unsaved work exists — drives BOTH the leave-warning and the Save button's enabled state. */
  const [dirty, setDirty] = useState(false);

  const saveConceptualization = async (
    changedValues: Partial<typeof initialValues>,
  ) => {
    const { schemas, dob, ...valuesToSave } = changedValues;

    if (!clientId) {
      return;
    }

    let toSave: Partial<typeof initialValues> = {
      ...valuesToSave,
    };

    if (dob) {
      toSave.dob = dob || null;
    }

    if (Object.keys(toSave).length === 0) {
      return;
    }

    try {
      const res = await upsertConceptualization({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'upsertConceptualization',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'ConceptualizationInput',
              value: {
                data: toSave,
                where: {
                  clientId,
                },
              },
            },
          },
        },
      });
      message.success(t('pages.conceptualization.saveSuccess'));
    } catch (error) {
      // `message.success` for an ERROR was the old behaviour — a failed save announced itself as a
      // saved one. With an explicit Save that also clears the unsaved-work guard, this has to
      // rethrow so `onSave` keeps the guard up and leaves Save offered.
      message.error(t('pages.conceptualization.saveError'));
      Sentry.captureException(error);
      throw error;
    }
  };

  useActivityTracking(
    conceptualizationData?.data?._id ||
      // @ts-ignore
      upsertConceptualizationData?.data?._id ||
      `${dbCols.conceptualizations}/create`,
  );

  useEffect(() => {
    // @ts-ignore
    if (upsertConceptualizationData?.data?._id) {
      if (!conceptualizationData?.data?._id) {
        therapistActivitySend({
          type: 'CREATED',
          // @ts-ignore
          resource: upsertConceptualizationData?.data?._id,
        });
      } else {
        therapistActivitySend({
          type: 'UPDATED',
          // @ts-ignore
          resource: upsertConceptualizationData?.data?._id,
        });
      }
    }
  }, [upsertConceptualizationData]);

  const { innerWidth } = useWindowSize();

  useEffect(() => {
    if ((innerWidth || 0) > 1024) {
      const handle = setInterval(() => {
        updateXarrow();
      }, 200);
      return () => clearInterval(handle);
    }
  }, [innerWidth]);

  const filter = (inputValue: string, path: DefaultOptionType[]) => {
    return path.some(
      (option) =>
        (option.value as string)
          .toLowerCase()
          .indexOf(inputValue.toLowerCase()) > -1,
    );
  };

  /**
   * EXPLICIT SAVE (founder decision, 2026-09-01, applied per-tool as each epic lands). This form
   * used to autosave every change on a 700 ms debounce with the Save button HIDDEN, which wrote a
   * therapist's keystrokes into the event log — one event per typing pause, each carrying the full
   * case formulation, so the stream recorded drafts instead of clinical decisions.
   *
   * NOTE it also autosaved only the CHANGED fields; an explicit save sends the whole form, which is
   * what the upsert expects and what "Save" means to the person clicking it.
   */
  const onValuesChange = useCallback(() => {
    setDirty(true);
    setWarnWhen(true);
  }, [setWarnWhen]);

  const onSave = useCallback(async () => {
    try {
      await saveConceptualization(form.getFieldsValue());
    } catch {
      // still unsaved: keep the guard up and leave Save offered
      return;
    }
    setDirty(false);
    setWarnWhen(false);
  }, [form, setWarnWhen]);

  const getLocale = useGetLocale();

  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  const treeData = useMemo(() => {
    return recursivelyDisableIfChildren(
      t('icd10', {
        returnObjects: true,
      }) as any[],
    );
  }, [currentLocale]);

  const {
    mutate: updateClientDiagnoses,
    data: updateClientDiagnosesData,
    isLoading: updateClientDiagnosesLoading,
    error: updateProgressNoteClientDiagnosesError,
  } = useCustomMutation<{ client: User }>();

  const updateClient = async (values: string[]) => {
    if (!clientId || !values) {
      return;
    }

    const res = await updateClientDiagnoses({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'updateClientDiagnoses',
        fields: ['_id'],
        variables: {
          input: {
            name: 'input',
            type: 'UpdateClientDiagnosesInput',
            value: {
              data: values,
              where: {
                id: clientId,
              },
            },
          },
        },
      },
    });
  };

  return (
    <Create
      title={t('pages.conceptualization.title')}
      saveButtonProps={{
        'onClick': onSave,
        'loading': upsertConceptualizationLoading,
        // nothing to save is not a thing to offer: the button follows the dirty flag that also
        // drives the leave-warning
        'disabled': !clientId || !dirty,
        'data-testid': 'cc-conceptualization--save',
      }}
      isLoading={clientsLoading || conceptualizationLoading || clientLoading}
    >
      <Form<typeof initialValues>
        form={form}
        initialValues={{
          ...initialValues,
          firstName: client?.firstName || '',
          lastName: client?.lastName || '',
        }}
        onFinish={(values) => {}}
        onValuesChange={onValuesChange}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 16 }}
        labelWrap
        data-testid="cc-edit-conceptualization"
      >
        <div className={styles['rowContainer']}>
          <SelectClient clientId={clientId} setClientId={setClientId} />
        </div>
        <div className={styles['rowContainer']}>
          <Card
            style={{ width: '100%' }}
            title={t('pages.conceptualization.clientProfile')}
            extra={
              <div>
                <Tooltip title={t('pages.conceptualization.piiTip')}>
                  <QuestionCircleOutlined style={{ margin: '0 5px' }} />
                </Tooltip>
                <Button
                  icon={
                    (innerWidth || 0) < 500 ? null : obfuscate ? (
                      <EyeOutlined />
                    ) : (
                      <EyeInvisibleOutlined />
                    )
                  }
                  onClick={() => setObfuscate(!obfuscate)}
                  size="small"
                  data-testid="cc-edit-conceptualization-toggle-obfuscate"
                >
                  {t(
                    obfuscate
                      ? 'pages.conceptualization.showInfo'
                      : 'pages.conceptualization.hideInfo',
                  )}
                </Button>
              </div>
            }
          >
            {obfuscate ? (
              <Skeleton active>
                <Skeleton.Input active size="small" />
                <Skeleton.Input active size="small" />
                <Skeleton.Input active size="small" />
              </Skeleton>
            ) : (
              <div>
                <div
                  style={{
                    display: 'flex',
                    width: '100%',
                  }}
                >
                  <Descriptions>
                    <Descriptions.Item label="ID">
                      {(client?._id || '')?.replace('users/', '')}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('pages.clientsShow.email')}>
                      {client?.email || ''}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
                <div className={styles['row-container--card']}>
                  <Form.Item
                    label={t('pages.conceptualization.fields.firstName')}
                    name="firstName"
                    className={styles['card-form-item']}
                  >
                    <Input
                      data-testid="cc-edit-conceptualization-firstName"
                      placeholder={t(
                        'pages.conceptualization.fields.firstName',
                      )}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('pages.conceptualization.fields.lastName')}
                    name="lastName"
                    className={styles['card-form-item']}
                  >
                    <Input
                      data-testid="cc-edit-conceptualization-lastName"
                      placeholder={t('pages.conceptualization.fields.lastName')}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('pages.conceptualization.fields.dob')}
                    name="dob"
                    className={styles['card-form-item']}
                  >
                    <DateFnsDatePicker
                      showToday={false}
                      data-testid="cc-edit-conceptualization-dob"
                    />
                  </Form.Item>
                </div>
                <div className={styles['row-container--card']}>
                  <Form.Item
                    label={t('pages.conceptualization.fields.gender')}
                    name="gender"
                    className={styles['card-form-item']}
                  >
                    <Select
                      data-testid="cc-edit-conceptualization-gender"
                      placeholder={t('pages.conceptualization.fields.gender')}
                      options={(
                        t('pages.conceptualization.genderOptions', {
                          returnObjects: true,
                        }) as string[]
                      ).map((o, idx) => ({
                        value: idx,
                        label: o,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('pages.conceptualization.fields.education')}
                    name="education"
                    className={styles['card-form-item']}
                  >
                    <Select
                      data-testid="cc-edit-conceptualization-education"
                      placeholder={t(
                        'pages.conceptualization.fields.education',
                      )}
                      options={(
                        t('pages.conceptualization.educationOptions', {
                          returnObjects: true,
                        }) as string[]
                      ).map((o, idx) => ({
                        value: idx,
                        label: o,
                      }))}
                    />
                  </Form.Item>
                  <Form.Item
                    label={t('pages.conceptualization.fields.maritalStatus')}
                    name="maritalStatus"
                    className={styles['card-form-item']}
                  >
                    <Select
                      data-testid="cc-edit-conceptualization-maritalStatus"
                      placeholder={t(
                        'pages.conceptualization.fields.maritalStatus',
                      )}
                      options={(
                        t('pages.conceptualization.maritalStatusOptions', {
                          returnObjects: true,
                        }) as string[]
                      ).map((o, idx) => ({
                        value: idx,
                        label: o,
                      }))}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  label={t('pages.conceptualization.fields.clientProfileOther')}
                  name="clientProfileOther"
                  style={{ flex: 1 }}
                >
                  <Input.TextArea
                    data-testid="cc-edit-conceptualization-clientProfileOther"
                    autoSize={{ minRows: 4, maxRows: 10 }}
                    placeholder={t(
                      'pages.conceptualization.fields.clientProfileOther',
                    )}
                    onResize={(e) => {
                      triggerScroll();
                      return e;
                    }}
                  />
                </Form.Item>
              </div>
            )}
          </Card>
        </div>

        <Divider />

        <Xwrapper>
          <div className={styles['rowContainer']}>
            <Form.Item
              label={t('pages.conceptualization.fields.geneticPredisposition')}
              name="geneticPredisposition"
              style={{ flex: 1 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-geneticPredisposition"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t(
                  'pages.conceptualization.fields.geneticPredisposition',
                )}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
          </div>

          <div className={styles['rowContainer']}>
            <Form.Item
              label={t('pages.conceptualization.fields.negativePastExperience')}
              name="negativePastExperience"
              style={{ flex: 1 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-negativeExperience"
                id="negativeExperience"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t(
                  'pages.conceptualization.fields.negativePastExperience',
                )}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
          </div>

          <div className={styles['rowContainerTense']}>
            <Form.Item
              label={
                <div>
                  <div>{t('pages.conceptualization.fields.deepBeliefs')}</div>

                  <Form.Item name="schemas">
                    <Cascader
                      data-testid="cc-edit-conceptualization-schemas"
                      style={{
                        maxWidth: 160,
                      }}
                      options={options}
                      onChange={(value, selectedOptions) => {
                        const currentBeliefs =
                          form.getFieldValue('deepBeliefs');
                        const selectedValues = selectedOptions
                          .map((option) => option.value)
                          .join(' -> ');
                        const newBeliefs = currentBeliefs
                          ? currentBeliefs + '\n' + selectedValues
                          : selectedValues;
                        form.setFieldValue('deepBeliefs', newBeliefs);
                        form.setFieldValue('schemas', '');
                      }}
                      placeholder={t('pages.conceptualization.schemaSearch')}
                      showSearch={{ filter }}
                      expandTrigger="hover"
                      size="small"
                    />
                  </Form.Item>
                </div>
              }
              name="deepBeliefs"
              style={{ flex: 3 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-deepBeliefs"
                id="beliefs"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t(
                  'pages.conceptualization.deepBeliefsPlaceholder',
                )}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
            <Form.Item
              label={t('pages.conceptualization.fields.triggerEvent')}
              name="triggerEvent"
              style={{ flex: 2 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-triggerEvent"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t('pages.conceptualization.fields.triggerEvent')}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
          </div>

          <div className={styles['rowContainer']}>
            <Form.Item
              label={t('pages.conceptualization.fields.lifeRules')}
              name="lifeRules"
              style={{ flex: 1, margin: '30px 0' }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-lifeRules"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t('pages.conceptualization.fields.lifeRules')}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
          </div>
          <div className={styles['rowContainer']}>
            <Form.Item
              label={t('pages.conceptualization.fields.triggers')}
              name="triggers"
              style={{ flex: 1 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-triggers"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t('pages.conceptualization.fields.triggers')}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>

            <Form.Item
              label={t('pages.conceptualization.fields.resources')}
              name="resources"
              style={{ flex: 1 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-resources"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t('pages.conceptualization.fields.resources')}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
          </div>

          <div className={styles['rowContainerSelect']} id="problems">
            <Form.Item
              name="problems"
              label={t('pages.conceptualization.fields.problems')}
              style={{ flex: 1 }}
            >
              <Select
                data-testid="cc-edit-conceptualization-problems"
                mode="tags"
                placeholder={t('profile.selectOrType')}
                size="large"
                onBlur={(e) => {
                  triggerScroll();
                  return e;
                }}
                onChange={(value) => {
                  triggerScroll();
                  return value;
                }}
                onDropdownVisibleChange={(open) => {
                  triggerScroll();
                  return open;
                }}
              >
                {portalEn.profile.clientIssues.map((i: string, idx) => (
                  <Select.Option key={i} value={i}>
                    {
                      t(`profile.clientIssues`, {
                        returnObjects: true,
                      })[idx]
                    }
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>

          <div className={styles['rowContainer']}>
            <Form.Item
              label={t('pages.conceptualization.fields.goals')}
              name="goals"
              style={{ flex: 1 }}
            >
              <Input.TextArea
                data-testid="cc-edit-conceptualization-goals"
                autoSize={{ minRows: 4, maxRows: 10 }}
                placeholder={t('pages.conceptualization.fields.goals')}
                onResize={(e) => {
                  triggerScroll();
                  return e;
                }}
              />
            </Form.Item>
          </div>
          <div className={styles['rowContainer']}>
            <Form.Item
              label={t('pages.sessions.diagnosesLabel')}
              tooltip={t('pages.sessions.icd10Hint')}
              style={{ flex: 1 }}
            >
              <TreeSelect
                data-testid="cc-edit-conceptualization-diagnoses"
                showSearch
                style={{ width: '100%' }}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                placeholder={t('pages.sessions.selectOrSearch')}
                value={diagnoses}
                allowClear
                onChange={async (values) => {
                  setDiagnoses(values as string[]);
                  await updateClient(values as string[]);
                }}
                treeData={treeData}
                treeLine
                treeCheckable
                popupMatchSelectWidth
                treeNodeFilterProp="title"
                tagRender={({ label, value, closable, onClose }) => {
                  const index = diagnoses?.findIndex(
                    (diagnosis) => diagnosis === value,
                  );
                  return (
                    <Tag
                      closable={closable}
                      onClose={onClose}
                      className={styles['diagnosis--tag']}
                    >
                      <b>
                        {t('pages.sessions.diagnoses', {
                          returnObjects: true,
                        })[index] || ''}
                        :
                      </b>{' '}
                      {label}
                    </Tag>
                  );
                }}
                {...((diagnoses?.length || 0) > 2 && {
                  open: false,
                  onDropdownVisibleChange: (open) => {
                    if (open) {
                      message.warning(t('pages.sessions.diagnosesLimit'));
                    }
                  },
                })}
              />
            </Form.Item>
          </div>

          {(innerWidth || 0) > 1300 && (
            <>
              <Xarrow
                start="negativeExperience"
                end="beliefs"
                startAnchor="bottom"
                endAnchor="top"
                {...xarrowProps}
              />

              <Xarrow
                start="triggerEvent"
                end="lifeRules"
                endAnchor="top"
                {...xarrowProps}
              />

              <Xarrow
                start="beliefs"
                end="lifeRules"
                startAnchor="bottom"
                endAnchor="top"
                {...xarrowProps}
              />

              <Xarrow
                start="lifeRules"
                end="problems"
                endAnchor="top"
                {...xarrowProps}
              />

              <Xarrow
                start="triggers"
                end="problems"
                startAnchor="bottom"
                endAnchor="top"
                {...xarrowProps}
              />

              <Xarrow
                start="resources"
                end="problems"
                startAnchor="bottom"
                endAnchor="top"
                {...xarrowProps}
              />

              <Xarrow
                start="problems"
                end="goals"
                endAnchor="top"
                {...xarrowProps}
              />
            </>
          )}
        </Xwrapper>
      </Form>
    </Create>
  );
};
